/**
 * Soft-launch gating.
 *
 * We are rolling the admin panel out to the client feature by feature, confirming
 * each one before it becomes reachable. This module is the SINGLE place that decides
 * what is unlocked at the current stage — the sidebar and the `_panel` route guard
 * both read from here, nothing else hard-codes the locked/unlocked split.
 *
 * To unlock a feature: add its nav `to` path to {@link UNLOCKED_ROUTES}.
 * To end the soft launch entirely: set {@link SOFT_LAUNCH_ENABLED} to `false`
 * (or remove this module and its usages) — the full panel comes back unchanged.
 */
export const SOFT_LAUNCH_ENABLED = true

/**
 * Nav routes (matching the `to` values in the sidebar in `routes/_panel.tsx`)
 * that are reachable during the soft launch. Everything else is greyed out in the
 * sidebar and redirected away by the `_panel` guard.
 */
export const UNLOCKED_ROUTES = new Set<string>([
  '/szkoly',
  '/katalog-rspo',
  '/klasy',
  '/nauczyciele',
  '/moje-szkoly',
  '/pytania',
  '/tematy',
  '/baza-wiedzy',
  '/quizy-automatyczne',
  '/plan-nauczania',
  // Compatibility redirects lead to the fixed catalog and central pricing screen.
  '/misje',
  '/zadania',
  '/konta-administracyjne',
  '/bledy-systemowe',
  '/eksport-danych',
  '/profil',
  '/moduly',
  '/rankingi',
  '/punktacja',
  '/powiadomienia',
  '/slowniki',
])

/** Whether a given nav `to` path is reachable at the current stage. */
export function isRouteUnlocked(to: string): boolean {
  return !SOFT_LAUNCH_ENABLED || UNLOCKED_ROUTES.has(to)
}

/**
 * Reduce any pathname to its top-level section path, e.g. `/szkoly/12` → `/szkoly`.
 * Used by the route guard so detail routes inherit their section's lock state.
 */
export function sectionOf(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean)[0]
  return segment ? `/${segment}` : '/'
}
